import { useState } from "react";
import { ChevronDown, Crown, Link2Off, RefreshCw, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { timeAgo } from "@/lib/api";
import DnsRecordTable from "./DnsRecordTable";
import RegistrarGuide from "./RegistrarGuide";
import { DomainProblems, DomainSteps, RemovalNote, SslLine, StateBadge, VariantNote } from "./DomainParts";
import { sslProblem } from "./helpers";

export default function DomainCard({ d, target, busy, onAction }) {
  const [open, setOpen] = useState(!d.serving);
  const [ask, setAsk] = useState(false);
  const t = `domain-${d.host.replace(/[^a-z0-9]/g, "-")}`;
  const primary = d.role === "primary";
  return (
    <article className="space-y-4 rounded-xl border border-slate-200 bg-white p-5" data-testid={t}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="break-all font-mono text-base font-semibold text-slate-900" data-testid={`${t}-host`}>{d.host}</h3>
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-medium text-white" data-testid={`${t}-role`}>{primary ? <><Crown size={11} />Principale</> : "Rimanda al principale (301)"}</span>
            <StateBadge d={d} testid={`${t}-state`} />
          </div>
          <p className="mt-1 text-[11px] text-slate-400" data-testid={`${t}-last-check`}>{d.last_check ? `Ultimo controllo ${timeAgo(d.last_check)}` : "Non ancora controllato"}{d.method === "txt" && " · certificato anticipato con record TXT"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {sslProblem(d) && <Button size="sm" variant="outline" disabled={busy} onClick={() => onAction("retry", d)} data-testid={`${t}-retry`}><RefreshCw size={13} className="mr-1" />Riprova certificato</Button>}
          {!primary && d.serving && <Button size="sm" variant="outline" disabled={busy} onClick={() => onAction("primary", d)} data-testid={`${t}-make-primary`}><Star size={13} className="mr-1" />Rendi principale</Button>}
          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" disabled={busy} onClick={() => setAsk(true)} data-testid={`${t}-disconnect`}><Link2Off size={13} className="mr-1" />Scollega</Button>
        </div>
      </div>
      <DomainSteps d={d} testid={`${t}-steps`} />
      <DomainProblems d={d} testid={`${t}-problems`} />
      <div className="space-y-1.5"><SslLine d={d} testid={`${t}-ssl`} /><VariantNote d={d} testid={`${t}-variant`} /><RemovalNote d={d} testid={`${t}-removal`} /></div>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="inline-flex items-center gap-1 text-sm font-medium text-sky-700 hover:text-sky-900" data-testid={`${t}-toggle-records`}>
          <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />Record DNS e istruzioni per il registrar</CollapsibleTrigger>
        <CollapsibleContent className="mt-3 space-y-4">
          <DnsRecordTable records={d.records} apex={d.apex} testid={`${t}-records`} />
          <RegistrarGuide d={d} target={target} />
        </CollapsibleContent>
      </Collapsible>
      <ConfirmDialog open={ask} onOpenChange={setAsk} destructive confirmLabel="Scollega" onConfirm={() => { setAsk(false); onAction("disconnect", d); }}
        title={`Scollegare ${d.host}?`}
        description={primary ? "Il dominio (e la sua variante) viene rimosso da Cloudflare e smette subito di mostrare il sito. Il sito resta online sull'indirizzo della piattaforma; contenuti e storico restano."
          : "Questo indirizzo smette di rimandare al dominio principale. Il dominio principale resta attivo."} />
    </article>
  );
}
